import React, { useState } from 'react';
import { TableCell, TableHead, TableRow } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import RolesPermissionsSelect from './components/RolesPermissionsSelect';
import LocationsFilter from './components/LocationsFilter';
import SortCell from './components/SortCell';
import FilterCell from './components/FilterCell';

import { useTranslation } from 'services/localization/useTranslation';
import { SortDirection, SortKey, SortKeys, UsersTableFilters } from './utils';

interface Props {
	sortDirection: SortDirection;
	sortKey: SortKey;
	filter: UsersTableFilters;
	onSort: (key: SortKey) => void;
	onFilter: (filter: UsersTableFilters) => void;
}

const UsersTableHead = (props: Props) => {
	const { sortDirection, sortKey, filter, onSort, onFilter } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const [rolesAnchorEl, setRolesAnchorEl] = useState<null | HTMLElement>(null);
	const [locationsAnchorEl, setLocationsAnchorEl] = useState<null | HTMLElement>(null);

	const handleFilterChange = (updatedFilter: Partial<UsersTableFilters>) => {
		onFilter({ ...filter, ...updatedFilter });
	};

	const renderRolesPermissionFilter = () => (
		<RolesPermissionsSelect
			open={!!rolesAnchorEl}
			value={{ roles: filter.roles, permissions: filter.permissions }}
			onChange={handleFilterChange}
			onClose={() => setRolesAnchorEl(null)}
			anchorEl={rolesAnchorEl}
		/>
	);

	const renderLocationsFilter = () => (
		<LocationsFilter
			open={!!locationsAnchorEl}
			value={filter.locations}
			onChange={(locationsFilter) => {
				handleFilterChange({ locations: locationsFilter });
			}}
			onClose={() => setLocationsAnchorEl(null)}
			anchorEl={locationsAnchorEl}
		/>
	);

	return (
		<TableHead className={classes.tableHead}>
			<TableRow className={classes.tableRow}>
				<SortCell
					id={SortKeys.email}
					label={t('users.user', 'User')}
					sortDirection={sortDirection}
					sortKey={sortKey}
					onSort={onSort}
				/>
				<FilterCell
					label={t('users.role', 'Role')}
					filterCount={filter.roles.length + filter.permissions.length}
					setAnchorEl={setRolesAnchorEl}
					renderFilter={renderRolesPermissionFilter}
				/>
				<FilterCell
					label={t('common:shopLocation.stores')}
					filterCount={filter.locations?.length ?? 0}
					setAnchorEl={setLocationsAnchorEl}
					renderFilter={renderLocationsFilter}
				/>
				<TableCell className={classes.noStretch} />
			</TableRow>
		</TableHead>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tableHead: {
			backgroundColor: theme.palette.background.secondary,
			whiteSpace: 'nowrap',
			'& > *': {
				verticalAlign: 'baseline',
			},
		},
		tableRow: {
			'& > *': {
				color: theme.palette.colors.tundra.main,
				borderBottom: `1px solid ${theme.palette.border.table}`,
				whiteSpace: 'nowrap',
				'&:first-child': {
					paddingLeft: theme.spacing(4),
					width: '40%',
				},
				'&:last-child': {
					paddingRight: theme.spacing(4),
				},
			},
		},
		noStretch: {
			width: '1%',
		},
	}),
);

export default UsersTableHead;
